import { Loading } from "../../app/Status";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { CompanyDto, LegacyNameDto, LegacyResolveBody } from "@klantpunt/shared";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { errMsg, useDirectoryRefresh } from "./DirectoryDialogs";

export const legacyKey = ["directory-legacy"] as const;
export const useLegacyNames = (enabled: boolean) =>
  useQuery({ queryKey: legacyKey, queryFn: () => api<LegacyNameDto[]>("/directory/legacy-names"), enabled });

const sources = (n: LegacyNameDto) =>
  [
    n.tasks && `${n.tasks} ${n.tasks === 1 ? "taak" : "taken"}`,
    n.obligations && `${n.obligations} ${n.obligations === 1 ? "keuring" : "keuringen"}`,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * *Gevonden in bestaande gegevens* (docs/ADRESBOEK.md, stap 3): namen die bij taken en keuringen als tekst
 * zijn ingevuld. Per naam: nieuw bedrijf, koppelen aan een bestaand bedrijf, of negeren. De tekst blijft staan.
 */
export function LegacyNames({ onOpenCompany }: { onOpenCompany: (id: string) => void }) {
  const { data, isPending } = useLegacyNames(true);
  const companies = useQuery({ queryKey: ["companies", "active"], queryFn: () => api<CompanyDto[]>("/companies") });
  if (isPending) return <Loading page />;
  return (
    <section className="panel dir-legacy" aria-label="Gevonden in bestaande gegevens">
      <p className="muted small">
        Deze namen staan als tekst bij taken en keuringen. Maak er een bedrijf van of koppel ze aan een bestaand
        bedrijf; dan kies je ze voortaan uit het adresboek. De ingevulde tekst blijft staan.
      </p>
      {data?.length ? (
        <ul className="dir-items">
          {data.map((n) => (
            <LegacyRow
              key={n.text.toLowerCase()}
              n={n}
              companies={companies.data ?? []}
              onOpenCompany={onOpenCompany}
            />
          ))}
        </ul>
      ) : (
        <p className="muted small">Niets meer te doen: alle namen zijn verwerkt.</p>
      )}
    </section>
  );
}

function LegacyRow({
  n,
  companies,
  onOpenCompany,
}: {
  n: LegacyNameDto;
  companies: CompanyDto[];
  onOpenCompany: (id: string) => void;
}) {
  const qc = useQueryClient();
  const refresh = useDirectoryRefresh();
  const toast = useToast();
  const [target, setTarget] = useState(n.match?.id ?? "");
  const [busy, setBusy] = useState(false);
  async function resolve(body: Omit<LegacyResolveBody, "text">, ok: string) {
    setBusy(true);
    try {
      const res = await api<{ companyId: string | null; names: LegacyNameDto[] }>("/directory/legacy-names", {
        method: "POST",
        body: { text: n.text, ...body },
      });
      qc.setQueryData(legacyKey, res.names);
      await refresh();
      toast(ok);
      if (body.action === "create" && res.companyId) onOpenCompany(res.companyId);
    } catch (err) {
      toast(errMsg(err));
      setBusy(false);
    }
  }
  const selectId = `legacy-${n.text.toLowerCase().replace(/\W+/g, "-")}`;
  return (
    <li className="dir-legacyrow">
      <span className="grow">
        <strong>{n.text}</strong>
        <div className="muted small">{sources(n)}</div>
        {n.match && <div className="small">Lijkt op {n.match.name}</div>}
      </span>
      <span className="dir-legacyacts">
        <label className="sr-only" htmlFor={selectId}>
          Bedrijf voor {n.text}
        </label>
        <select id={selectId} value={target} onChange={(e) => setTarget(e.target.value)} disabled={busy}>
          <option value="">Koppelen aan…</option>
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn small"
          disabled={busy || !target}
          onClick={() => void resolve({ action: "link", companyId: target }, "Gekoppeld")}
        >
          Koppelen
        </button>
        <button
          type="button"
          className="btn small primary"
          disabled={busy}
          onClick={() => void resolve({ action: "create" }, "Bedrijf gemaakt")}
        >
          Nieuw bedrijf
        </button>
        <button
          type="button"
          className="btn small ghost"
          disabled={busy}
          onClick={() => void resolve({ action: "ignore" }, "Genegeerd")}
        >
          Negeren
        </button>
      </span>
    </li>
  );
}
